import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RecentActivity, DashboardCourse } from '../../types';
import { api } from '../../services/api';
import Navbar from '../shared/Navbar';
import { formatTimeAgo } from '../../utils/time';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<DashboardCourse[]>([]);
  const [organisationName, setOrganisationName] = useState<string | null>(null);
  const [removingCourseId, setRemovingCourseId] = useState<number | null>(null);
  const [courseError, setCourseError] = useState<string | null>(null);
  const [latestActivity, setLatestActivity] = useState<RecentActivity | null>(null);
  const [flashcardCount, setFlashcardCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const me = await api.me();
        if (!me?.authenticated) {
          navigate('/login');
          return;
        }
        setOrganisationName(me.user.organisation_name ?? null);
        const data = await api.getDashboard();
        setCourses(data.courses);
        setLatestActivity(data.latest_activity);
        setFlashcardCount(data.flashcard_count);
      } catch (e: any) {
        if (e?.message?.includes('401') || e?.message?.includes('Authentication')) {
          navigate('/login');
        } else {
          setError(e?.message || 'Failed to load dashboard');
        }
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [navigate]);

  const handleRemove = async (courseId: number) => {
    setRemovingCourseId(courseId);
    setCourseError(null);
    try {
      await api.removeMyCourse(courseId);
      setCourses(prev => prev.flatMap(course => {
        if (course.id !== courseId) return [course];
        return course.organisation_access
          ? [{ ...course, is_saved: false, my_course_id: null, saved_at: null }]
          : [];
      }));
    } catch (err: any) {
      setCourseError(err?.message || 'Failed to remove saved course');
    } finally {
      setRemovingCourseId(null);
    }
  };

  const renderActivityCard = (activity: RecentActivity) => {
    if (activity.type === 'lesson') {
      const isCompleted = activity.completed;
      const actionLabel = isCompleted ? 'Review lesson →' : 'Continue lesson →';
      const lessonPath = `/lesson/${activity.lesson_id}/${activity.chat_id}`;
      return (
        <div className="tm-surface activity-card">
          <div className="activity-card-main">
            <div className="activity-card-text">
              <div className="activity-card-title">{activity.lesson_title}</div>
              <div className="activity-card-meta">
                {activity.course_title && <span>{activity.course_title} · </span>}
                {formatTimeAgo(activity.last_activity_at)}
              </div>
            </div>
            {isCompleted && (
              <span className="activity-card-badge activity-card-badge--done">Completed</span>
            )}
          </div>
          <div className="activity-card-actions">
            <Link to={lessonPath} className="tm-button tm-button--secondary activity-card-action">
              {actionLabel}
            </Link>
            {activity.course_id && (
              <Link to={`/course/${activity.course_id}`} className="tm-button tm-button--secondary activity-card-action activity-card-action--secondary">
                Course overview →
              </Link>
            )}
          </div>
        </div>
      );
    }

    // exam
    const isInProgress = activity.state === 'IN_PROGRESS';
    const actionLabel = isInProgress ? 'Continue exam →' : 'Review exam →';
    const examPath = `/exam/${activity.exam_id}/${activity.attempt_id}`;
    const stateLabel = activity.state === 'IN_PROGRESS'
      ? 'In progress'
      : activity.state === 'SUBMITTED'
        ? 'Submitted'
        : 'Marked';
    return (
      <div className="tm-surface activity-card">
        <div className="activity-card-main">
            <div className="activity-card-text">
              <div className="activity-card-title">{activity.exam_name}</div>
              <div className="activity-card-meta">
                {activity.course_title && <span>{activity.course_title} · </span>}
                {formatTimeAgo(activity.last_activity_at)}
              </div>
            </div>
          <span className={`activity-card-badge activity-card-badge--${activity.state.toLowerCase()}`}>
            {stateLabel}
          </span>
        </div>
        <div className="activity-card-actions">
          <Link to={examPath} className="tm-button tm-button--secondary activity-card-action">
            {actionLabel}
          </Link>
          {activity.course_id && (
            <Link to={`/course/${activity.course_id}`} className="tm-button tm-button--secondary activity-card-action activity-card-action--secondary">
              Course overview →
            </Link>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="app">
      <Navbar />

      <main className="app-main">
        <div className="content-container">
          {latestActivity && (
            <div className="dashboard-top-row">
              <div className="continue-section">
                {renderActivityCard(latestActivity)}
              </div>

              <div className="flashcard-panel">
                <div className="tm-surface flashcard-panel-card">
                  <div className="flashcard-panel-count">
                    <span className="flashcard-panel-number">{flashcardCount}</span>
                    <span className="flashcard-panel-label">
                      {flashcardCount === 1 ? 'flashcard' : 'flashcards'} in your deck
                    </span>
                  </div>
                  <Link to="/flashcards" className="tm-button tm-button--secondary flashcard-panel-btn">
                    Review flashcards →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {!latestActivity && flashcardCount > 0 && (
            <div className="continue-section">
              <div className="tm-surface flashcard-panel-card flashcard-panel-card--standalone">
                <div className="flashcard-panel-count">
                  <span className="flashcard-panel-number">{flashcardCount}</span>
                  <span className="flashcard-panel-label">
                    {flashcardCount === 1 ? 'flashcard' : 'flashcards'} in your deck
                  </span>
                </div>
                <Link to="/flashcards" className="tm-button tm-button--secondary flashcard-panel-btn">
                  Review flashcards →
                </Link>
              </div>
            </div>
          )}

          <div className="dashboard-header">
            <div className="dashboard-header-row">
              <h2 className="dashboard-title">Your Courses</h2>
              <Link to="/browse" className="tm-button tm-button--secondary dashboard-browse-btn">Browse All Courses <span className="dashboard-browse-arrow">→</span></Link>
            </div>
            <p className="dashboard-subtitle">
              {organisationName
                ? `Courses you’ve saved and courses provided by ${organisationName}.`
                : 'Courses you’ve saved for easy access.'}
            </p>
          </div>

          {courseError && <div className="error" role="alert">{courseError}</div>}
          {loading ? (
            <div className="loading">Loading…</div>
          ) : error ? (
            <div className="error">Error: {error}</div>
          ) : courses.length === 0 ? (
            <div className="empty-state">
              <p>{organisationName
                ? 'No saved courses or organisation courses yet.'
                : "You haven't saved any courses yet."}</p>
              <Link to="/subjects" className="dashboard-browse-link">Find courses for me →</Link>
            </div>
          ) : (
            <div className="dashboard-list">
              {courses.map(course => {
                const total = course.lesson_count + course.exam_count;
                const totalStarted = course.lessons_started + course.exams_attempted;
                const totalCompleted = course.lessons_completed + course.exams_completed;
                const completedPct = total > 0 ? Math.round((totalCompleted / total) * 100) : 0;
                const startedPct  = total > 0 ? Math.round((totalStarted  / total) * 100) : 0;

                return (
                  <div key={course.id} className="dashboard-list-row" onClick={() => navigate(`/course/${course.id}`)}>
                    <div className="dashboard-course-info">
                      <Link to={`/course/${course.id}`} className="dashboard-list-title" onClick={event => event.stopPropagation()}>
                        {course.title}
                      </Link>
                      <div className="dashboard-course-sources">
                        {course.is_saved && <span className="dashboard-source dashboard-source--saved">Saved by you</span>}
                        {course.organisation_access && (
                          <span className="dashboard-source dashboard-source--organisation" title={organisationName ?? undefined}>
                            Via organisation
                          </span>
                        )}
                      </div>
                    </div>
                    {total > 0 && (
                      <div className="dashboard-progress-bar-track" role="progressbar"
                        aria-label={`${course.title} completion`} aria-valuenow={completedPct} aria-valuemin={0} aria-valuemax={100}>
                        <div className="dashboard-progress-bar-fill dashboard-progress-bar-started" style={{ width: `${startedPct}%` }} />
                        <div className="dashboard-progress-bar-fill dashboard-progress-bar-completed" style={{ width: `${completedPct}%` }} />
                      </div>
                    )}
                    {course.is_saved && (
                      <button className="dashboard-remove-btn" title="Remove from saved courses"
                        aria-label={`Remove ${course.title} from saved courses`}
                        disabled={removingCourseId !== null}
                        onClick={event => {
                          event.stopPropagation();
                          void handleRemove(course.id);
                        }} type="button">
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
